import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import StaffStatusEvolutionFilters from './components/StaffStatusEvolutionFilters';
import StaffStatusEvolutionList from './components/StaffStatusEvolutionList';
import { IFilters } from './types';
import { downloadStatusEvolution, fetchStatusEvolution } from '../../api/staff-status-evolution';
import ContentWrapper from '../../components/ContentWrapper';
import ProgressSection from '../../components/ProgressSection';
import { IStaffStatusEvolution } from '../../types/staff-status-evolution.type';
import { HttpUtils } from '../../utils/http.utils';
import { DEFAULT_FILTERS } from '../StaffEvolution/constants';

const StaffStatusEvolution = React.memo(() => {
  const [filters, setFilters] = useState<IFilters>(DEFAULT_FILTERS);
  const [evolution, setEvolution] = useState<IStaffStatusEvolution[]>([]);
  const [isSubmitProcess, setSubmitProcess] = useState<boolean>(false);
  const [isExportProcess, setExportProcess] = useState<boolean>(false);
  const [isReady, setReady] = useState<boolean>(false);

  const { t } = useTranslation();

  const handleSubmit = React.useCallback(
    (data: IFilters) => {
      setFilters(data);
      setSubmitProcess(true);

      if (!isReady) {
        setReady(true);
      }

      fetchStatusEvolution(data)
        .then((response) => {
          setEvolution(response);
        })
        .catch(() => setEvolution([]))
        .finally(() => setSubmitProcess(false));
    },
    [isReady],
  );

  const handleExport = React.useCallback(
    (data: IFilters) => {
      setFilters(data);

      setExportProcess(true);

      if (!isReady) {
        setReady(true);
      }

      downloadStatusEvolution(data)
        .then((response) => HttpUtils.handleBlob(response, 'status_evolution.xlsx'))
        .finally(() => setExportProcess(false));
    },
    [isReady],
  );

  const renderList = React.useMemo(() => {
    if (!isReady) {
      return null;
    }

    if (isSubmitProcess) {
      return <ProgressSection mt={3} />;
    }

    return <StaffStatusEvolutionList evolution={evolution} filters={filters} />;
  }, [evolution, filters, isReady, isSubmitProcess]);

  return (
    <ContentWrapper title={t('staff_status_evolution.title')}>
      <StaffStatusEvolutionFilters
        onSubmit={handleSubmit}
        onExport={handleExport}
        isSubmitProcess={isSubmitProcess}
        isExportProcess={isExportProcess}
      />

      {renderList}
    </ContentWrapper>
  );
});

export default StaffStatusEvolution;
